import type {
  ActivitySummary,
  AuditEvent,
  GatewayKey,
  GatewayStatus,
  HTTPStats,
  MeterRecord,
  ObservedTokenTotal,
  ObservedUsage,
  ObservedUsageGroup,
  ObservedUsageRow,
  QuotaView,
  Relay,
  RelayStatusView,
  RuntimePanel,
  TransformationExecution,
} from "../model";
import { observedUsageWindowDefinition } from "../model";

export class InvalidResponseError extends Error {
  constructor(message = "Admin response does not match the expected contract") {
    super(message);
    this.name = "InvalidResponseError";
  }
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new InvalidResponseError();
  }
  return value as UnknownRecord;
}

function string(value: unknown): string {
  if (typeof value !== "string") throw new InvalidResponseError();
  return value;
}

function optionalString(value: unknown): string | null {
  if (value === undefined || value === null || value === "") return null;
  return string(value);
}

function number(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new InvalidResponseError();
  }
  return value;
}

function nonNegativeInteger(value: unknown): number {
  const parsed = number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) throw new InvalidResponseError();
  return parsed;
}

function optionalNumber(value: unknown): number | null {
  if (value === undefined || value === null) return null;
  return number(value);
}

function optionalNonNegativeInteger(value: unknown): number | null {
  if (value === undefined || value === null) return null;
  return nonNegativeInteger(value);
}

function boolean(value: unknown): boolean {
  if (typeof value !== "boolean") throw new InvalidResponseError();
  return value;
}

function exactDecimal(value: unknown): string {
  const parsed = string(value);
  if (!/^(?:0|[1-9][0-9]*)$/u.test(parsed)) throw new InvalidResponseError();
  return parsed;
}

function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw new InvalidResponseError();
  return value;
}

function enumValue<const T extends readonly string[]>(value: unknown, values: T): T[number] {
  if (typeof value !== "string" || !values.includes(value)) {
    throw new InvalidResponseError();
  }
  return value as T[number];
}

function adaptHTTPStats(value: unknown): HTTPStats {
  const raw = record(value);
  return {
    requestsTotal: number(raw.requests_total),
    inFlight: number(raw.in_flight),
    canceled: number(raw.canceled),
    status2xx: number(raw.status_2xx),
    status4xx: number(raw.status_4xx),
    status5xx: number(raw.status_5xx),
    otherStatus: number(raw.other_status),
    responseBytes: number(raw.response_bytes),
  };
}

function adaptTransformation(value: unknown): TransformationExecution {
  const raw = record(value);
  return {
    workers: nonNegativeInteger(raw.workers),
    active: nonNegativeInteger(raw.active),
    queueDepth: nonNegativeInteger(raw.queue_depth),
    queueCapacity: nonNegativeInteger(raw.queue_capacity),
  };
}

function adaptRuntimePanel(value: unknown): RuntimePanel {
  if (value === undefined || value === null) return { status: "unavailable" };
  let raw: UnknownRecord;
  try {
    raw = record(value);
  } catch {
    return { status: "invalid" };
  }
  const schemaVersion = typeof raw.schema_version === "number" && Number.isFinite(raw.schema_version)
    ? raw.schema_version
    : null;
  const runtimeKind = typeof raw.runtime_kind === "string" ? raw.runtime_kind : null;
  if (schemaVersion !== 1 || runtimeKind !== "rust") {
    return { status: "unsupported", schemaVersion, runtimeKind };
  }
  try {
    const process = record(raw.process);
    const execution = record(raw.execution);
    const transformation = execution.transformation === undefined
      ? null
      : adaptTransformation(execution.transformation);
    const requestBodyDecoder = execution.request_body_decoder === undefined
      ? null
      : adaptTransformation(execution.request_body_decoder);
    const databaseRaw = execution.database === undefined ? null : record(execution.database);
    return {
      status: "available",
      snapshot: {
        schemaVersion: 1,
        runtimeKind: "rust",
        collectionStatus: enumValue(raw.collection_status, ["complete", "partial"] as const),
        process: {
          uptimeMS: nonNegativeInteger(process.uptime_ms),
          residentMemoryBytes: optionalNonNegativeInteger(process.resident_memory_bytes),
          virtualMemoryBytes: optionalNonNegativeInteger(process.virtual_memory_bytes),
          cpuUserMS: optionalNonNegativeInteger(process.cpu_user_ms),
          cpuSystemMS: optionalNonNegativeInteger(process.cpu_system_ms),
          threads: optionalNonNegativeInteger(process.threads),
          openFileDescriptors: optionalNonNegativeInteger(process.open_file_descriptors),
        },
        execution: {
          tasksActive: nonNegativeInteger(execution.tasks_active),
          ioWorkers: nonNegativeInteger(execution.io_workers),
          transformation,
          requestBodyDecoder,
          database: databaseRaw === null ? null : {
            readLanes: nonNegativeInteger(databaseRaw.read_lanes),
            readActive: nonNegativeInteger(databaseRaw.read_active),
            readQueueDepth: nonNegativeInteger(databaseRaw.read_queue_depth),
            readQueueCapacity: nonNegativeInteger(databaseRaw.read_queue_capacity),
            writeLanes: nonNegativeInteger(databaseRaw.write_lanes),
            writeActive: nonNegativeInteger(databaseRaw.write_active),
            writeQueueDepth: nonNegativeInteger(databaseRaw.write_queue_depth),
            writeQueueCapacity: nonNegativeInteger(databaseRaw.write_queue_capacity),
          },
        },
      },
    };
  } catch {
    return { status: "invalid" };
  }
}

export function adaptRelay(value: unknown): Relay {
  const raw = record(value);
  return {
    id: string(raw.id),
    displayName: string(raw.display_name),
    baseURL: string(raw.base_url),
    stateFingerprint: string(raw.state_fingerprint),
    state: enumValue(raw.state, ["active", "disabled", "retired"] as const),
    createdAtMS: number(raw.created_at_ms),
    updatedAtMS: number(raw.updated_at_ms),
  };
}

export function adaptRelays(value: unknown): Relay[] {
  const raw = record(value);
  return array(raw.relays).map(adaptRelay);
}

export function adaptGatewayKey(value: unknown): GatewayKey {
  const raw = record(value);
  const limits = record(raw.limits);
  return {
    id: string(raw.id),
    publicID: string(raw.public_id),
    label: string(raw.label),
    state: enumValue(raw.state, ["active", "disabled", "revoked"] as const),
    authVersion: number(raw.auth_version),
    limits: {
      concurrency: optionalNumber(limits.concurrency),
      rpm: optionalNumber(limits.rpm),
    },
    createdAtMS: number(raw.created_at_ms),
    updatedAtMS: number(raw.updated_at_ms),
    eligibleRelayIDs: array(raw.eligible_relay_ids).map(string),
  };
}

export function adaptGatewayKeys(value: unknown): GatewayKey[] {
  const raw = record(value);
  return array(raw.keys).map(adaptGatewayKey);
}

export function adaptCreatedGatewayKey(value: unknown): { key: GatewayKey; bearer: string } {
  const raw = record(value);
  return { key: adaptGatewayKey(raw.key), bearer: string(raw.bearer) };
}

export function adaptCreatedRelay(value: unknown): Relay {
  return adaptRelay(record(value).relay);
}

export function adaptGatewayStatus(value: unknown): GatewayStatus {
  const raw = record(value);
  const meter = record(raw.meter);
  let operational: GatewayStatus["operational"] = null;
  if (raw.operational !== undefined && raw.operational !== null) {
    const operation = record(raw.operational);
    const admission = record(operation.admission);
    const keyValues = admission.key_in_flight !== null
      && typeof admission.key_in_flight === "object"
      && !Array.isArray(admission.key_in_flight)
      ? Object.values(admission.key_in_flight as UnknownRecord).map(number)
      : null;
    operational = {
      http: adaptHTTPStats(operation.http),
      admission: {
        globalInFlight: number(admission.global_in_flight),
        activeKeys: admission.active_keys === undefined
          ? (keyValues?.length ?? 0)
          : number(admission.active_keys),
        keyInFlight: keyValues === null
          ? number(admission.key_in_flight)
          : keyValues.reduce((sum, value) => sum + value, 0),
        searchInFlight: number(admission.search_in_flight),
      },
      runtime: adaptRuntimePanel(operation.runtime),
    };
  }
  return {
    meter: {
      accepted: number(meter.accepted),
      queueDropped: number(meter.queue_dropped),
      hotWritten: number(meter.hot_written),
      hotWriteFailures: number(meter.hot_write_failures),
      hotDropped: number(meter.hot_dropped),
      coldArchived: number(meter.cold_archived),
      coldDropped: number(meter.cold_dropped),
      pending: number(meter.pending),
      coldAvailable: boolean(meter.cold_available),
      accepting: boolean(meter.accepting),
      writerHealthy: boolean(meter.writer_healthy),
      degraded: boolean(meter.degraded),
    },
    operational,
  };
}

export function adaptRelayStatus(value: unknown): RelayStatusView {
  const response = record(value);
  const raw = record(response.status);
  const operational = record(raw.operational);
  const admission = record(operational.admission);
  return {
    relayID: string(raw.relay_id),
    stateFingerprint: string(raw.state_fingerprint),
    credentialProvider: string(raw.credential_provider),
    upstreamAccountID: optionalString(raw.upstream_account_id),
    credentialGeneration: optionalNumber(raw.credential_generation),
    credentialExpiresAtMS: optionalNumber(raw.credential_expires_at_ms),
    credentialStatus: enumValue(raw.credential_status, ["available", "unavailable"] as const),
    operational: {
      http: adaptHTTPStats(operational.http),
      admission: {
        transformation: number(admission.transformation ?? admission.Transformation),
        operational: number(admission.operational ?? admission.Operational),
        waiting: number(admission.waiting ?? admission.Waiting),
      },
      transformation: adaptTransformation(operational.transformation),
      runtime: adaptRuntimePanel(operational.runtime),
    },
  };
}

export function adaptQuota(value: unknown): QuotaView {
  const response = record(value);
  const raw = record(response.quota);
  const snapshot = raw.snapshot === undefined || raw.snapshot === null ? null : record(raw.snapshot);
  const limits = snapshot
    ? array(snapshot.limits).map((limitValue) => {
        const limit = record(limitValue);
        return {
          id: string(limit.id),
          name: optionalString(limit.name),
          windows: array(limit.windows).map((windowValue) => {
            const window = record(windowValue);
            return {
              kind: string(window.kind),
              usedPercent: number(window.used_percent),
              durationSeconds: number(window.duration_seconds),
              resetsAt: number(window.resets_at),
            };
          }),
        };
      })
    : [];
  return {
    relayID: string(response.relay_id),
    status: enumValue(raw.status, ["fresh", "stale", "unavailable"] as const),
    observedAt: optionalString(raw.observed_at),
    planType: snapshot ? string(snapshot.plan_type) : null,
    limits,
    refreshFailed: raw.refresh_failure === "quota_refresh_failed",
  };
}

function adaptMeter(value: unknown): MeterRecord {
  const raw = record(value);
  const tokens = record(raw.tokens);
  return {
    requestID: string(raw.request_id),
    gatewayKeyID: string(raw.gateway_key_id),
    sessionBindingID: string(raw.session_binding_id),
    relayID: string(raw.relay_id),
    routeKind: enumValue(raw.route_kind, ["responses", "search"] as const),
    startedAtMS: number(raw.started_at_ms),
    durationMS: number(raw.duration_ms),
    ttfbMS: optionalNumber(raw.ttfb_ms),
    httpStatus: number(raw.http_status),
    outcome: enumValue(
      raw.outcome,
      [
        "completed",
        "relay_error",
        "relay_failure",
        "stream_error",
        "downstream_error",
        "canceled",
      ] as const,
    ),
    model: optionalString(raw.model),
    requestBytes: number(raw.request_bytes),
    responseBytes: number(raw.response_bytes),
    usageStatus: enumValue(raw.usage_status, ["reported", "missing", "parse_error"] as const),
    tokens: {
      input: optionalNumber(tokens.input),
      output: optionalNumber(tokens.output),
      total: optionalNumber(tokens.total),
      cacheRead: optionalNumber(tokens.cache_read),
      cacheWrite: optionalNumber(tokens.cache_write),
    },
  };
}

export function adaptMeters(value: unknown): MeterRecord[] {
  return array(record(value).meters).map(adaptMeter);
}

function adaptObservedTokenTotal(value: unknown): ObservedTokenTotal {
  const raw = record(value);
  const knownRecords = nonNegativeInteger(raw.known_records);
  const sum = raw.sum === null ? null : exactDecimal(raw.sum);
  if ((knownRecords === 0) !== (sum === null)) throw new InvalidResponseError();
  return { sum, knownRecords };
}

function adaptObservedUsageRow(value: unknown, groupBy: ObservedUsageGroup): ObservedUsageRow {
  const raw = record(value);
  const subject = record(raw.subject);
  let adaptedSubject: ObservedUsageRow["subject"];
  if (groupBy === "all") {
    if (subject.kind !== "all" || Object.keys(subject).length !== 1) throw new InvalidResponseError();
    adaptedSubject = { kind: "all" };
  } else if (groupBy === "relay") {
    if (subject.kind !== "relay" || Object.keys(subject).length !== 4) throw new InvalidResponseError();
    const internalID = string(subject.internal_id);
    const displayName = string(subject.display_name);
    if (internalID.length === 0 || displayName.length === 0) throw new InvalidResponseError();
    adaptedSubject = {
      kind: "relay",
      internalID,
      displayName,
      state: enumValue(subject.state, ["active", "disabled", "retired"] as const),
    };
  } else {
    if (subject.kind !== "key" || Object.keys(subject).length !== 5) throw new InvalidResponseError();
    const internalID = string(subject.internal_id);
    const publicID = string(subject.public_id);
    const label = string(subject.label);
    if (internalID.length === 0 || publicID.length === 0 || label.length === 0) throw new InvalidResponseError();
    adaptedSubject = {
      kind: "key",
      internalID,
      publicID,
      label,
      state: enumValue(subject.state, ["active", "disabled", "revoked"] as const),
    };
  }
  const requests = record(raw.requests);
  const usage = record(raw.usage);
  const tokens = record(raw.tokens);
  const adapted: ObservedUsageRow = {
    subject: adaptedSubject,
    requests: {
      total: nonNegativeInteger(requests.total),
      responses: nonNegativeInteger(requests.responses),
      search: nonNegativeInteger(requests.search),
      completed: nonNegativeInteger(requests.completed),
      abnormal: nonNegativeInteger(requests.abnormal),
    },
    usage: {
      reported: nonNegativeInteger(usage.reported),
      missing: nonNegativeInteger(usage.missing),
      parseError: nonNegativeInteger(usage.parse_error),
      reportedWithoutTotal: nonNegativeInteger(usage.reported_without_total),
    },
    tokens: {
      input: adaptObservedTokenTotal(tokens.input),
      output: adaptObservedTokenTotal(tokens.output),
      total: adaptObservedTokenTotal(tokens.total),
      cacheRead: adaptObservedTokenTotal(tokens.cache_read),
      cacheWrite: adaptObservedTokenTotal(tokens.cache_write),
    },
    tokenCoverage: null,
  };
  if (
    adapted.requests.responses + adapted.requests.search !== adapted.requests.total
    || adapted.requests.completed + adapted.requests.abnormal !== adapted.requests.total
    || adapted.usage.reported + adapted.usage.missing + adapted.usage.parseError
      !== adapted.requests.responses
    || adapted.usage.reportedWithoutTotal > adapted.usage.reported
  ) {
    throw new InvalidResponseError();
  }
  if (raw.token_coverage !== null) {
    const coverage = record(raw.token_coverage);
    const known = nonNegativeInteger(coverage.known);
    const eligible = nonNegativeInteger(coverage.eligible);
    if (
      eligible === 0
      || eligible !== adapted.requests.responses
      || known > eligible
      || known !== adapted.usage.reported - adapted.usage.reportedWithoutTotal
    ) {
      throw new InvalidResponseError();
    }
    adapted.tokenCoverage = { known, eligible };
  } else if (adapted.requests.responses !== 0) {
    throw new InvalidResponseError();
  }
  return adapted;
}

export function adaptObservedUsage(value: unknown): ObservedUsage {
  const raw = record(value);
  const window = observedUsageWindowDefinition(raw.window);
  if (window === null) throw new InvalidResponseError();
  const generatedAtMS = nonNegativeInteger(raw.generated_at_ms);
  const startAtMS = nonNegativeInteger(raw.start_at_ms);
  const endAtMS = nonNegativeInteger(raw.end_at_ms);
  const groupBy = enumValue(raw.group_by, ["all", "key", "relay"] as const);
  if (
    raw.schema_version !== 1
    || generatedAtMS !== endAtMS
    || endAtMS - startAtMS !== window.durationMS
  ) {
    throw new InvalidResponseError();
  }
  const hotHistory = record(raw.hot_history);
  const count = nonNegativeInteger(hotHistory.count);
  const capacity = nonNegativeInteger(hotHistory.capacity);
  if (capacity === 0) throw new InvalidResponseError();
  const oldestCompletionAtMS = optionalNonNegativeInteger(hotHistory.oldest_completion_at_ms);
  if ((count === 0) !== (oldestCompletionAtMS === null)) throw new InvalidResponseError();
  const rows = array(raw.rows);
  if (groupBy === "all" && rows.length !== 1) throw new InvalidResponseError();
  const adaptedRows = rows.map((row) => adaptObservedUsageRow(row, groupBy));
  if (groupBy === "key" || groupBy === "relay") {
    for (let index = 1; index < adaptedRows.length; index += 1) {
      const previous = adaptedRows[index - 1]!.subject;
      const current = adaptedRows[index]!.subject;
      if (
        previous.kind !== groupBy
        || current.kind !== groupBy
        || previous.internalID >= current.internalID
      ) {
        throw new InvalidResponseError();
      }
    }
  }
  return {
    schemaVersion: 1,
    window: window.value,
    groupBy,
    generatedAtMS,
    startAtMS,
    endAtMS,
    hotHistory: {
      count,
      capacity,
      oldestCompletionAtMS,
      windowStatus: enumValue(hotHistory.window_status, ["complete", "partial"] as const),
    },
    rows: adaptedRows,
  };
}

export function adaptAudit(value: unknown): AuditEvent[] {
  return array(record(value).audit).map((eventValue) => {
    const event = record(eventValue);
    let metadata: Record<string, unknown> | null = null;
    try {
      const parsed: unknown = JSON.parse(string(event.metadata_json));
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        metadata = parsed as Record<string, unknown>;
      }
    } catch {
      metadata = null;
    }
    return {
      id: number(event.id),
      action: string(event.action),
      subjectType: string(event.subject_type),
      subjectID: string(event.subject_id),
      result: string(event.result),
      metadata,
      createdAtMS: number(event.created_at_ms),
    };
  });
}

export function summarizeActivity(records: MeterRecord[]): ActivitySummary {
  let success = 0;
  let clientErrors = 0;
  let failures = 0;
  let knownTokenRecords = 0;
  let inputTokens = 0;
  let outputTokens = 0;
  let totalTokens = 0;
  let cacheReadTokens = 0;
  let cacheWriteTokens = 0;

  for (const item of records) {
    if (item.httpStatus >= 200 && item.httpStatus < 300) success += 1;
    else if (item.httpStatus >= 400 && item.httpStatus < 500) clientErrors += 1;
    else failures += 1;

    if (item.usageStatus === "reported" && item.tokens.total !== null) {
      knownTokenRecords += 1;
      inputTokens += item.tokens.input ?? 0;
      outputTokens += item.tokens.output ?? 0;
      totalTokens += item.tokens.total;
      cacheReadTokens += item.tokens.cacheRead ?? 0;
      cacheWriteTokens += item.tokens.cacheWrite ?? 0;
    }
  }

  return {
    records: records.length,
    success,
    clientErrors,
    failures,
    knownTokenRecords,
    tokenCoveragePercent: records.length === 0 ? null : (knownTokenRecords / records.length) * 100,
    inputTokens,
    outputTokens,
    totalTokens,
    cacheReadTokens,
    cacheWriteTokens,
  };
}
